import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { kommunikationApi } from '../api';

const STATUS_FARBEN = {
  Entwurf: '#64748b', geplant: '#f59e0b', versendet: '#3b82f6', zugestellt: '#22c55e',
  empfangen: '#10b981', gelesen: '#6366f1', beantwortet: '#22c55e', 'Antwort ausstehend': '#f97316',
  fehlgeschlagen: '#ef4444', archiviert: '#374151'
};

const RICHTUNGS_ICONS = { eingehend: '📨', ausgehend: '📤', intern: '📝' };

export default function KommunikationPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [richtungFilter, setRichtungFilter] = useState('');
  const [ueberfaelligFilter, setUeberfaelligFilter] = useState(false);
  const [tab, setTab] = useState('alle');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = {
        search: search || undefined,
        status: statusFilter || undefined,
        richtung: richtungFilter || undefined,
        ueberfaellig: ueberfaelligFilter ? '1' : undefined,
        page, pageSize,
      };
      if (tab === 'eingehend') params.richtung = 'eingehend';
      if (tab === 'ausgehend') params.richtung = 'ausgehend';
      if (tab === 'entwuerfe') params.status = 'Entwurf';
      if (tab === 'ueberfaellig') params.ueberfaellig = '1';
      const r = await kommunikationApi.list(params);
      setRows(r.rows || []); setTotal(r.total || 0);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [search, statusFilter, richtungFilter, ueberfaelligFilter, tab, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 20, color: '#f1f5f9', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>✉️ Kommunikation</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/kommunikation/telefonnotiz')} className="btn-secondary">📞 Telefonnotiz</button>
          <button onClick={() => navigate('/kommunikation/email-entwurf')} className="btn-secondary">✉️ E-Mail-Entwurf</button>
          <button onClick={() => navigate('/kommunikation/neu')} className="btn-primary">+ Neue Kommunikation</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, overflowX: 'auto' }}>
        {[
          { key: 'alle', label: 'Alle' },
          { key: 'eingehend', label: '📨 Eingehend' },
          { key: 'ausgehend', label: '📤 Ausgehend' },
          { key: 'entwuerfe', label: 'Entwürfe' },
          { key: 'ueberfaellig', label: '⚠️ Überfällig' },
        ].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setPage(1); }} style={{
            background: tab === t.key ? '#818cf8' : '#1e293b', color: tab === t.key ? '#fff' : '#94a3b8',
            border: '1px solid #334155', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13
          }}>{t.label}</button>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Betreff, Inhalt, Absender…" style={inputStyle} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">Alle Status</option>
          {['Entwurf','geplant','versendet','empfangen','gelesen','beantwortet','Antwort ausstehend','fehlgeschlagen'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {err && <div style={{ background: '#7f1d1d', padding: 12, borderRadius: 8, marginBottom: 12 }}>{err}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#6366f1', padding: 40 }}>⏳ Lade…</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>
          <div style={{ fontSize: 36 }}>✉️</div>
          <div style={{ marginTop: 10 }}>Keine Kommunikation gefunden.</div>
        </div>
      ) : (
        <>
          <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 8 }}>{total} Einträge</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {rows.map(k => (
              <div key={k.id} onClick={() => navigate(`/kommunikation/${k.id}`)}
                style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '12px 14px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 16 }}>{RICHTUNGS_ICONS[k.richtung] || '📩'}</span>
                  <span style={{ fontWeight: 500, flex: 1 }}>{k.betreff || '(kein Betreff)'}</span>
                  <span style={{ background: STATUS_FARBEN[k.status] || '#334155', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4 }}>{k.status}</span>
                </div>
                <div style={{ display: 'flex', gap: 16, color: '#64748b', fontSize: 12, flexWrap: 'wrap' }}>
                  <span>📅 {k.datum ? new Date(k.datum).toLocaleDateString('de-DE') : '—'}</span>
                  {k.anbieter_name && <span>🏢 {k.anbieter_name}</span>}
                  {k.vertrag_titel && <span>📋 {k.vertrag_titel}</span>}
                  <span>📡 {k.kanal}</span>
                  {k.antwort_faellig_am && k.antwort_status === 'offen' && (
                    <span style={{ color: new Date(k.antwort_faellig_am) < new Date() ? '#ef4444' : '#f59e0b' }}>
                      ⚠️ Antwort fällig: {new Date(k.antwort_faellig_am).toLocaleDateString('de-DE')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {total > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page === 1} className="btn-secondary">‹ Zurück</button>
              <span style={{ padding: '6px 12px', color: '#94a3b8', fontSize: 13 }}>{page} / {Math.ceil(total/pageSize)}</span>
              <button onClick={() => setPage(p => p+1)} disabled={page >= Math.ceil(total/pageSize)} className="btn-secondary">Weiter ›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const inputStyle = { background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 10px', borderRadius: 6, fontSize: 13, minWidth: 200 };
const selectStyle = { background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 10px', borderRadius: 6, fontSize: 13 };
